import { useAuthContext } from "../../auth/state/AuthContext";
import { InitialChecks, OptionType, TaskItem } from "../models/delivery-plan.interfaces";
import {
  addChecklistItem,
  addCustomTask,
  deleteOption,
  extractInitials,
  formatDate,
  getChecklist,
  resetToPreset,
  sendCheckboxStatus,
} from "../services/checklists.service";
import { PlanChecklist } from "./PlanChecklist";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SingleValue } from "react-select";

// TODO We could create a shared component to handle the loading state
const PreDeliveryPlan = () => {
  const { firstname, lastname } = useAuthContext().state.user?.accessToken || {};
  const fullName = firstname + " " + lastname;
  const { tcCode } = useParams();
  const [isLoading, setLoading] = useState(false);
  const [items, setItems] = useState<TaskItem[]>([]);
  const [CheckedItems, SetCheckedItems] = useState<InitialChecks>({});
  const [options] = useState([]);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const [inputText, setInputText] = useState<string>("");
  const [showInput, setShowInput] = useState(false);
  const { sub: email } = useAuthContext().state.user?.accessToken || {};

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const { filteredData, initialCheckedState } = await getChecklist(tcCode, " ", "Pre-delivery plan");

      setItems(filteredData);
      SetCheckedItems(initialCheckedState);
      setLoading(false);
    } catch (error: unknown) {
      setLoading(false);
    }
  }, [tcCode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCheckboxChange = debounce((taskId: number, item: TaskItem) => {
    SetCheckedItems((prev) => {
      const isCurrentlyChecked = prev[taskId];
      const newCheckedState = {
        ...prev,
        [taskId]: !isCurrentlyChecked,
      };

      const updatedItem = {
        ...item,
        IS_CHECKED: isCurrentlyChecked ? "N" : "Y",
      };

      sendCheckboxStatus(updatedItem, tcCode, " ", fullName, setLoading, fetchData, email ?? "");

      return newCheckedState;
    });
  }, 300);

  const handleSelectAddButtonClick = async () => {
    if (!selectedOption) {
      console.error("No option selected");
      return;
    }

    try {
      setLoading(true);
      const output = await addChecklistItem(tcCode, " ", "Pre-delivery plan", selectedOption, fullName, email ?? "");

      if (output.success === true) {
        fetchData();
        setSelectedOption(null);
        setLoading(false);
      }
    } catch (error: unknown) {
      setLoading(false);
    }
  };

  const handleSelectChange = (option: SingleValue<OptionType>) => {
    setSelectedOption(option as OptionType);
  };

  const predeliveryDeleteItem = async (item: TaskItem) => {
    try {
      setLoading(true);
      const output = await deleteOption(tcCode, " ", item, fullName);

      if (output.success === true) {
        fetchData();
        setLoading(false);
      }
    } catch (error: unknown) {
      setLoading(false);
    }
  };

  const ResetToPreset = async (item: TaskItem) => {
    try {
      setLoading(true);
      const output = await resetToPreset(tcCode, " ", item, fullName);

      if (output.success === true) {
        fetchData();
        setLoading(false);
      }
    } catch (error: unknown) {
      setLoading(false);
    }
  };

  const AddCustomInputTask = async () => {
    if (!inputText.trim()) return;
    setSelectedOption(null);

    try {
      setLoading(true);
      const output = await addCustomTask(tcCode, " ", "Pre-delivery plan", inputText, fullName);

      if (output.success === true) {
        fetchData();
        setLoading(false);
        setShowInput(false);
      }
    } catch (error: unknown) {
      setLoading(false);
    }
  };

  const customInputViewCancel = () => {
    setShowInput(false);
  };

  const checkedCount = Object.values(CheckedItems).filter((isChecked) => isChecked).length;

  return PlanChecklist(
    isLoading,
    checkedCount,
    items,
    CheckedItems,
    handleCheckboxChange,
    extractInitials,
    formatDate,
    ResetToPreset,
    predeliveryDeleteItem,
    showInput,
    handleInputChange,
    AddCustomInputTask,
    customInputViewCancel,
    options,
    setShowInput,
    handleSelectChange,
    selectedOption,
    handleSelectAddButtonClick,
  );
};

export default PreDeliveryPlan;
